import { useState, useEffect, useCallback, useMemo } from "react";
import { PaymentService, PayeeAccountVM, PayeeAccountTypesEnum } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import {
  resolveAllFilterCount,
  resolveOrphanedAggregateFilter,
  shouldResolveOrphanedAggregateFilter,
  useAggregateFilterCounts,
} from "../../../hooks/useAggregateFilterCounts";
import { buildPayeeAccountFilter, ALL_FILTER_KEY } from "./payeeAccountFilters";
import { useDebounce } from "use-debounce";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "../../../newComponents/form-fields/InputField";
import SelectField from "../../../newComponents/form-fields/SelectField";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import PayeeAccountsTopHeader from "./PayeeAccountsTopHeader";
import PayeeAccountCardItem from "./PayeeAccountCardItem";
import { GetImages } from "../../../utils/GetImages";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";
import { defaultPageSize } from "../../../constants/constants";
import { getApiPayeeAccountOrganizationlistCounts } from "../../../services/listFilterCounts.service";

interface PayeeAccountsProps {
  className?: string;
}

const PayeeAccountsPage = ({ className = "" }: PayeeAccountsProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [selectedPayeeAccount, setSelectedPayeeAccount] = useState<PayeeAccountVM | undefined>(undefined);
  const [payeeTypeOptions, setPayeeTypeOptions] = useState<{ Id: number; Name: string }[]>([]);
  const [isLoadingPayeeTypes, setIsLoadingPayeeTypes] = useState(false);
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const check = usePermissions();
  const canWrite = check.canWrite("PayeeAccounts");

  // For Empty State
  const [hasAnyPayeeAccounts, setHasAnyPayeeAccounts] = useState<boolean>(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const rowsFilter = buildPayeeAccountFilter(activeFilter, debouncedSearchTerm);
  const searchOnlyFilter = debouncedSearchTerm
    ? buildPayeeAccountFilter(ALL_FILTER_KEY, debouncedSearchTerm)
    : null;

  const handleFilterChange = useCallback((filter: string) => {
    setActiveFilter((current) => (current === filter ? current : filter));
  }, []);

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
    aggregateValues,
  } = infiniteScrollHook({
    httpFetchDataFn: PaymentService.getApiPayeeAccountOrganizationlist,
    FullyQualifiedFilters: rowsFilter,
    pageSize: defaultPageSize,
  });

  const fetchCounts = useCallback(async (filter: string | null) => {
    const res = await apiHelper(
      getApiPayeeAccountOrganizationlistCounts,
      { showNotification: false },
      organizationId,
      defaultPageSize,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      null,
    );
    return res?.data?.aggregateValues ?? undefined;
  }, [organizationId]);

  const {
    aggregateValues: aggregateCounts,
    baselineAggregateValues,
    loading: countsLoading,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `payee-accounts:${organizationId}`,
  });

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: resolveAllFilterCount(aggregateCounts) },
        ...(aggregateCounts
          ? Object.keys(aggregateCounts)
              .filter((title) => title !== ALL_FILTER_KEY)
              .map((title) => ({ title }))
          : []),
      ]),
    [aggregateCounts, getVisibleOptions],
  );

  const handleFullRefresh = useCallback(() => {
    refreshCounts();
    fetchData(1);
  }, [refreshCounts, fetchData]);

  // When zero-count pills are hidden (e.g. the last payee account in a filtered bucket was
  // just deleted), the currently selected pill can become an orphan - rows would otherwise
  // stay filtered to that now-invisible bucket forever, showing "No Payee Accounts Found"
  // instead of falling back to All. Mirrors service-request/index.tsx's identical guard.
  // Wait for counts so a loading-time All-only pill set cannot reset deep-linked filters.
  useEffect(() => {
    if (!shouldResolveOrphanedAggregateFilter(countsLoading, aggregateCounts)) {
      return;
    }
    const resolved = resolveOrphanedAggregateFilter(activeFilter, visibleFilterOptions);
    if (resolved !== activeFilter) {
      setActiveFilter(resolved);
    }
  }, [countsLoading, aggregateCounts, visibleFilterOptions, activeFilter]);

  useEffect(() => {
    // Tolerate a failed/in-flight counts fetch (apiHelper swallows the error) by also
    // trusting the row-list's own count, so a single failing counts call can't hide the
    // whole page behind the empty state while rows actually loaded fine.
    const baselineAllCount = resolveAllFilterCount(baselineAggregateValues ?? aggregateCounts);
    setHasAnyPayeeAccounts(baselineAllCount > 0 || rowCount > 0);
  }, [aggregateCounts, baselineAggregateValues, rowCount]);

  useEffect(() => {
    setIsLoadingPayeeTypes(true);
    PaymentService.getApiPayeeAccountPayeeTypes()
      .then((response) => {
        const options = response?.success?.map((item: any) => ({
          Id: item.id,
          Name: item.name || "Unknown",
        })) || [];
        setPayeeTypeOptions(options);
        setIsLoadingPayeeTypes(false);
      })
      .catch((error) => {
        console.error("Error fetching payee types:", error);
        setIsLoadingPayeeTypes(false);
      });
  }, []);

  const methods = useForm<PayeeAccountVM>({
    defaultValues: {
      id: undefined,
      accountName: "",
      payeeAccountTypeId: undefined, // ✅ FIX 1: undefined rakho taake required validation kaam kare
      organizationId: organizationId,
    },
    mode: "onBlur",
  });

  const { handleSubmit, reset, formState: { isDirty, isValid } } = methods;

  const onSubmit = async (data: PayeeAccountVM) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        id: data.id,
        accountName: data.accountName,
        organizationId: String(organizationId),
        payeeAccountTypeId: Number(data.payeeAccountTypeId),
      };

      const apiCall = data.id
        ? PaymentService.putApiPayeeAccount
        : PaymentService.postApiPayeeAccount;

      await apiHelper(
        apiCall,
        {
          title: data.id
            ? "Payee Account Updated"
            : "Payee Account Added",
          successMessage: data.id
            ? "Payee account updated successfully"
            : "Payee account added successfully",
          failureMessage: data.id
            ? "Unable to update payee account"
            : "Unable to add payee account",
          showNotification: true,
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />
        },
        payload
      );

      setAddModalOpen(false);
      reset();
      handleFullRefresh();
    } catch (error) {
      console.error("Error submitting payee account:", error);
    }
    finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isAddModalOpen) {
      if (selectedPayeeAccount) {
        // Edit Mode - existing data se populate karo
        methods.reset(selectedPayeeAccount);
      } else {
        // Add Mode - payeeAccountTypeId undefined rakho
        methods.reset({
          id: undefined,
          accountName: "",
          payeeAccountTypeId: undefined, // ✅ FIX 2: Add mode mein undefined
          organizationId: organizationId,
        });
      }
    }
  }, [isAddModalOpen, selectedPayeeAccount, methods, organizationId]);

  const handleEditPayeeAccount = (payeeAccount: PayeeAccountVM) => {
    setSelectedPayeeAccount(payeeAccount);
    methods.reset(payeeAccount);
    setAddModalOpen(true);
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setSelectedPayeeAccount(undefined);
    reset({
      id: undefined,
      accountName: "",
      payeeAccountTypeId: undefined, // ✅ FIX 3: Close par bhi undefined
      isOrganizationAccount: true,
      organizationId: organizationId,
      entityId: organizationId,
    });
  };

  const formId = selectedPayeeAccount ? "edit-payee-account-form" : "add-payee-account-form";

  const isAccountTypeReadOnly =
    Boolean(selectedPayeeAccount?.id) &&
    (
      (
        selectedPayeeAccount?.payeeAccountTypeId != null &&
        (
          Number(selectedPayeeAccount.payeeAccountTypeId) === PayeeAccountTypesEnum.OrganizationUser ||
          Number(selectedPayeeAccount.payeeAccountTypeId) === PayeeAccountTypesEnum.Contractor
        )
      ) ||
      Boolean((selectedPayeeAccount as PayeeAccountVM & { hasLinkedPlaidPaymentMethod?: boolean }).hasLinkedPlaidPaymentMethod)
    );

  return (
    <PermissionGuard permission="PayeeAccounts" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className={`payee-accounts-page content-wrapper container-fluid ${className}`}>
        {hasAnyPayeeAccounts && (
          <PayeeAccountsTopHeader
            PageHeading="Payee Accounts"
            Count={rowCount}
            ButtonName={canWrite ? "Add Payee Account" : undefined}
            MobileButtonName={canWrite ? "Add" : undefined}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activeFilter={activeFilter}
            handleFilterChange={handleFilterChange}
            onClick={() => {
              setSelectedPayeeAccount(undefined);
              reset();
              setAddModalOpen(true);
            }}
            filterOptions={visibleFilterOptions}
          />
        )}

        <PayeeAccountCardItem
          dataHookState={{
            rows,
            loading,
            hasMore,
            fetchData,
            currentPage,
            handleFullRefresh,
          }}
          onEditPayeeAccount={canWrite ? handleEditPayeeAccount : undefined}
          className="organisation-listing-card"
          hasAnyPayeeAccounts={hasAnyPayeeAccounts}
        />

        <RightSideModal
          isOpen={isAddModalOpen}
          onClose={handleModalClose}
          title={selectedPayeeAccount ? "Edit Payee Account" : "Add Payee Account"}
          buttonlabel={"Save"}
          formId={formId}
          maxWidth={550}
          isDirty={isDirty}
          isSubmitDisabled={isSubmitting || !isDirty || !isValid}
          isLoading={isSubmitting}
        >
          <Card className="no-bg">
            <FormProvider {...methods}>
              <form id={formId} onSubmit={handleSubmit(onSubmit, (error) => {
                console.log("Form validation errors:", error);
              })}>
                <div className="row">
                  <div className="col-12">
                    <InputField
                      name="accountName"
                      label="Payee Name"
                      type="text"
                      required
                      requiredMessage="Payee name is required"
                      placeholder="Enter Payee Name"
                      allowSpecialChars={false}
                      onlyAlphabets={true}
                      validation={{
                        required: "Payee name is required",
                        minLength: {
                          value: 3,
                          message: "Payee name must be at least 3 characters",
                        },
                        maxLength: {
                          value: 40,
                          message: "Payee name cannot exceed 40 characters",
                        },
                      }}
                    />
                  </div>
                  <div className="col-md-12">
                    <SelectField
                      placeholderText="Select Account Type"
                      name="payeeAccountTypeId"
                      label="Account Type"
                      required
                      requiredMessage="Account Type is required"
                      options={payeeTypeOptions}
                      disabled={isLoadingPayeeTypes || isAccountTypeReadOnly}
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
          </Card>
        </RightSideModal>
      </div>
    </PermissionGuard>
  );
};

export default PayeeAccountsPage;